import React, { useMemo, useState } from 'react';
import { useTournament } from 'context/TournamentContext';
import { useSocket } from 'utils/hooks/useSocket';
import { formatCurrency } from 'utils';
import { getPriceFromCache, isCacheValid } from 'utils/priceCache';
import { useTournamentPrizePayout } from 'hooks/useTournamentPrizePayout';
import { mapPrizePayoutCurrencyLabel } from 'types/tournamentPrizePayout';
import copyIconImg from 'assets/Icon/Copyicon.svg';

import {
  Wrap,
  RewardTitle,
  RewardAmount,
  RewardUsd,
  RewardSpinner,
  RewardStatus,
  RewardSubStatus,
  InfoSection,
  InfoLabel,
  InfoValueRow,
  InfoValue,
  CopyWrap,
  CopyButton,
  CopiedText,
  ContactText,
  Title,
  SubTitle,
} from '../EndPage/styled';

import {
  SeeDetailsLink,
  CheckmarkIcon,
  GasFeeRow,
  GasFeeLabel,
  GasFeeValue,
} from './styled';

const CancelRefundView = () => {
  const { tournamentDetails, tournamentStatus, joinedStatus } = useTournament();
  const { socket } = useSocket() as { socket: any };
  const rawData: any = tournamentDetails?.data;
  const attributes = rawData?.attributes ?? rawData?.data ?? rawData ?? {};
  const tournamentId = rawData?.id;
  const tournamentIdString = tournamentId != null ? String(tournamentId) : '';
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const hasPaidStake = Number(attributes?.stake) > 0;
  const isCancelRefundEligible = joinedStatus === true && hasPaidStake;

  const {
    payout,
    viewState: payoutViewState,
    isLoading: isPayoutLoading,
  } = useTournamentPrizePayout({
    tournamentId: tournamentIdString,
    isWinner: false,
    socket,
    enabled: tournamentStatus === 'CANCELLED',
    isCancelRefundEligible,
  });

  const refundAmount = payout ? Number(payout.amount) : 0;

  const cachedUsdApprox = useMemo(() => {
    if (refundAmount <= 0) return null;
    const cached = getPriceFromCache();
    if (!isCacheValid(cached)) return null;
    return refundAmount * cached!.price;
  }, [refundAmount]);

  const gasFee = payout?.gas_fee_deducted != null ? Number(payout.gas_fee_deducted) : null;

  const formatPol = (value: number): string => {
    if (value === 0) return '0.00';
    const abs = Math.abs(value);
    let decimals = 2;
    if (abs < 1) decimals = 4;
    if (abs < 0.01) decimals = 6;
    const str = value.toFixed(decimals);
    const [intPart, decPart] = str.split('.');
    const trimmed = decPart.replace(/0+$/, '');
    const final = trimmed.length < 2 ? trimmed.padEnd(2, '0') : trimmed;
    return intPart + '.' + final;
  };

  const handleCopy = async (value: string, field: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(prev => prev === field ? null : prev), 2000);
    } catch {
      // Ignore clipboard errors
    }
  };

  const polygonScanUrl = payout?.tx_hash
    ? `https://polygonscan.com/tx/${payout.tx_hash}`
    : null;

  const truncateHash = (hash: string) =>
    hash.length <= 16 ? hash : hash.slice(0, 15) + '...';

  const currencyLabel = mapPrizePayoutCurrencyLabel(payout?.currency ?? 'POL');

  const renderRefundStatus = () => {
    if (!isCancelRefundEligible) {
      return null;
    }

    if (isPayoutLoading && !payout) {
      return <RewardStatus>Loading refund status...</RewardStatus>;
    }

    if (payoutViewState === 'NOT_ELIGIBLE' || payoutViewState === 'NOT_WINNER') {
      return null;
    }

    if (payoutViewState === 'QUEUED' || payoutViewState === 'PENDING' || payoutViewState === 'INITIATED') {
      return (
        <>
          <RewardSpinner />
          <RewardSubStatus>Your funds are being sent...{'\n'}(This may take up to 10 min)</RewardSubStatus>
        </>
      );
    }

    if (payoutViewState === 'CONFIRMED') {
      return (
        <>
          <CheckmarkIcon>&#10003;</CheckmarkIcon>
          <RewardStatus>Your funds have been returned!</RewardStatus>
          {polygonScanUrl && (
            <SeeDetailsLink href={polygonScanUrl} target="_blank" rel="noopener noreferrer">
              See details
            </SeeDetailsLink>
          )}
        </>
      );
    }

    if (payoutViewState === 'FAILED') {
      return (
        <>
          <RewardStatus>Refund delayed</RewardStatus>
          <RewardSubStatus>
            {payout?.error_message || 'Retrying automatically...'}
          </RewardSubStatus>
        </>
      );
    }

    return null;
  };

  // No payout record — show simple cancelled message
  if (!isCancelRefundEligible) {
    return (
      <Wrap className="wrapModalEnd">
        <SubTitle>Tournament</SubTitle>
        <Title>
          <span className="down_state">Cancelled</span>
        </Title>
        <RewardStatus>This tournament has been cancelled.</RewardStatus>
      </Wrap>
    );
  }

  return (
    <Wrap className="wrapModalEnd">
      <SubTitle>Tournament cancelled</SubTitle>
      <Title>Your funds are being{'\n'}returned to your address</Title>

      <RewardTitle>Refunded stake:</RewardTitle>
      <RewardAmount>
        {formatPol(refundAmount)} {currencyLabel}
      </RewardAmount>
      {cachedUsdApprox != null && (
        <RewardUsd>{'\u2248'}{parseFloat(Number(cachedUsdApprox).toFixed(4))} USD</RewardUsd>
      )}

      {renderRefundStatus()}

      {gasFee != null && gasFee > 0 && (
        <GasFeeRow>
          <GasFeeLabel>Transaction fee:</GasFeeLabel>
          <GasFeeValue>-{formatPol(gasFee)} {currencyLabel}</GasFeeValue>
        </GasFeeRow>
      )}

      {payout?.tx_hash && (
        <InfoSection>
          <InfoLabel>Transaction ID:</InfoLabel>
          <InfoValueRow>
            <InfoValue>{truncateHash(payout.tx_hash)}</InfoValue>
            <CopyWrap>
              {copiedField === 'txHash' && <CopiedText>Copied!</CopiedText>}
              <CopyButton
                type="button"
                aria-label="Copy transaction id"
                onClick={() => handleCopy(payout.tx_hash!, 'txHash')}
              >
                <img src={copyIconImg} alt="Copy" />
              </CopyButton>
            </CopyWrap>
          </InfoValueRow>
        </InfoSection>
      )}

      <InfoSection>
        <InfoLabel>Game number:</InfoLabel>
        <InfoValueRow>
          <InfoValue>{tournamentIdString}</InfoValue>
          <CopyWrap>
            {copiedField === 'gameNumber' && <CopiedText>Copied!</CopiedText>}
            <CopyButton
              type="button"
              aria-label="Copy game number"
              onClick={() => handleCopy(tournamentIdString, 'gameNumber')}
            >
              <img src={copyIconImg} alt="Copy" />
            </CopyButton>
          </CopyWrap>
        </InfoValueRow>
      </InfoSection>

      <ContactText>
        If you encounter any issues with receiving your refund, please contact us at{' '}
        <strong>contact@tradingbattles.com</strong>{' '}
        and provide your transaction ID and game number
      </ContactText>
    </Wrap>
  );
};

export default CancelRefundView;
